import { isEqual, pick } from 'lodash';
import { useMemo } from 'react';
import { FieldSectionType } from 'api/field-sections/types';
import {
  FormChoiceOption,
  FormQuestion,
  FormQuestionGroup,
  FormQuestionMetaValue,
  FormQuestionType,
} from 'api/form/types';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { isNativeField } from 'components/organisms/module-settings/fields/utils';
import { getArbitraryColor } from 'components/organisms/templates/forms/form-builder/field-properties/options/color-dropdown';
import { useFieldsQueryFilter } from 'queries';
import { useGetFlattenedFormQuestions } from 'queries/form-template';
import { Field } from 'stores/fields';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import {
  ChoiceQuestionType,
  DefaultCurrencyCode,
  DropdownQuestionType,
  FormQuestionsList,
} from './constants';

export interface RowQuestionDefaultValue {
  formQuestionType: FormQuestionType;
  questionText: string;
  description: string;
  required: boolean;
  placeholder: string;
  templateId: number;
  position: number;
}
export interface DefaultFormQuestionValues
  extends Omit<FormQuestion, 'questionId' | 'options' | 'rows'> {
  options?: Omit<FormChoiceOption, 'optionId' | 'position'>[];
  templateId: number;
  rows?: RowQuestionDefaultValue[];
}

export const defaultAllowedTaskFieldsList = [
  'taskName',
  'taskDescription',
  'Priority',
];

export const constructDefaultQuestionValues = ({
  type,
  branchingFrom,
  position,
  templateId,
}: {
  type: FormQuestionType;
  branchingFrom?: number;
  position?: number;
  templateId: number;
}): DefaultFormQuestionValues => {
  const defaultQuestionValues = {
    questionText: 'Question',
    formQuestionType: type,
    required: false,
    templateId,
  } as DefaultFormQuestionValues;

  if (ChoiceQuestionType.includes(type)) {
    const option = {
      optionLabel: 'Option 1',
    } as FormChoiceOption;

    if (DropdownQuestionType.includes(type)) {
      option.color = getArbitraryColor(0);
    }
    defaultQuestionValues.options = [option];
  }

  if (type === FormQuestionType.Phone) {
    defaultQuestionValues.questionMeta = {
      type: FormQuestionMetaValue.Phone,
      countryCode: 1,
    };
  }
  if (type === FormQuestionType.Currency) {
    defaultQuestionValues.questionMeta = {
      type: FormQuestionMetaValue.Currency,
      currencySymbol: DefaultCurrencyCode,
    };
  }
  if (type === FormQuestionType.StarRating) {
    defaultQuestionValues.questionMeta = {
      type: FormQuestionMetaValue.StarRating,
      numberOfStar: 5,
    };
  }
  if (type === FormQuestionType.TextStatement) {
    defaultQuestionValues.description = 'Statement description';
  }
  if (
    type === FormQuestionType.CheckBoxGrid ||
    type === FormQuestionType.MultipleChoiceGrid
  ) {
    defaultQuestionValues.placeholder = 'Question 1';
    defaultQuestionValues.rows = [
      {
        formQuestionType: FormQuestionType.MatrixRow,
        questionText: 'Row 1',
        required: defaultQuestionValues.required,
        templateId,
      } as RowQuestionDefaultValue,
    ];
    defaultQuestionValues.options = [{ optionLabel: 'Column 1' }];
  }
  if (branchingFrom) {
    defaultQuestionValues.branchingFrom = branchingFrom;
  }

  if (position) {
    defaultQuestionValues.position = position;
  }

  return defaultQuestionValues;
};

export const isFormValid = (form) => {
  const validationErrors = form.getFieldsError();

  return !validationErrors.some((e) => e.errors.length);
};

export const isFormDirty = (formValues, initialValues, allowedProps) => {
  return !isEqual(
    pick(formValues, allowedProps),
    pick(initialValues, allowedProps),
  );
};

export type CFQuestionMap = Field & {
  formQuestionType: FormQuestionType;
  formQuestionGroup: FormQuestionGroup;
  formQuestionIcon: React.ReactNode;
};
export const mapProjectCustomFieldWithFormQuestion = (
  customFields,
): CFQuestionMap[] => {
  const updatedFormCustomFields = [] as CFQuestionMap[];

  customFields.forEach((field) => {
    let formQuestionType;

    switch (field.optionsAvailable ? field.fieldType : field.fieldDataType) {
      case 'NUMBER':
        formQuestionType = FormQuestionType.Number;
        break;
      case 'TEXT':
        formQuestionType = FormQuestionType.TextShort;
        break;
      case 'MULTI_LINE_TEXT':
        formQuestionType = FormQuestionType.TextLong;
        break;
      case 'DATE':
        formQuestionType = FormQuestionType.Date;
        break;
      case 'SINGLE_SELECT':
        formQuestionType = FormQuestionType.ChoiceDropdown;
        break;
      case 'MULTI_SELECT':
        formQuestionType = FormQuestionType.ChoiceDropdownMultiselect;
        break;
      case 'BOOLEAN':
        formQuestionType = FormQuestionType.ChoiceRadio;
        break;
      default:
    }

    if (field.fieldType === FieldTypes.Rating) {
      formQuestionType = FormQuestionType.StarRating;
    }

    const formQuestionMap = FormQuestionsList.find(
      (question) => question.value === formQuestionType,
    );

    if (formQuestionType && formQuestionMap) {
      updatedFormCustomFields.push({
        ...field,
        formQuestionType,
        formQuestionGroup: formQuestionMap.group,
        formQuestionIcon: formQuestionMap.icon,
      });
    }
  });

  return updatedFormCustomFields;
};

export const useFormTemplateProjectFieldsList = (templateId: number) => {
  const { data: projectCustomFields } = useProjectCustomFields();

  const flattenedQuestions = useGetFlattenedFormQuestions(templateId);

  const usedProjectCfIds = useMemo(() => {
    return (flattenedQuestions || []).reduce((acc: number[], question) => {
      if (question.projectFieldId) {
        return [...acc, question.projectFieldId];
      }

      return acc;
    }, []);
  }, [flattenedQuestions]);

  const availableProjectCustomFields = useMemo(() => {
    const availableCF = projectCustomFields.filter(
      (cf) => !usedProjectCfIds.includes(cf.fieldId) && cf.isEnabled,
    );

    return mapProjectCustomFieldWithFormQuestion(availableCF);
  }, [projectCustomFields, usedProjectCfIds]);

  const usedProjectCustomFields = useMemo(() => {
    const availableCF = projectCustomFields.filter(
      (cf) => usedProjectCfIds.includes(cf.fieldId) && cf.isEnabled,
    );

    return mapProjectCustomFieldWithFormQuestion(availableCF);
  }, [projectCustomFields, usedProjectCfIds]);

  return [availableProjectCustomFields, usedProjectCustomFields];
};

const taskField = (field): boolean =>
  field.objectType === FieldSectionType.Task;
const predicateAllowedFields = (field) =>
  !isNativeField(field) ||
  defaultAllowedTaskFieldsList.includes(field.fieldName);

const isEnabled = (field): boolean => field.isEnabled;
const taskFilters = [taskField, predicateAllowedFields, isEnabled];

export const useFormTemplateTaskFieldsList = (templateId: number) => {
  const { data: taskFields = [] } = useFieldsQueryFilter(taskFilters);

  const flattenedQuestions = useGetFlattenedFormQuestions(templateId);

  const usedProjectCfIds = useMemo(() => {
    return (flattenedQuestions || []).reduce((acc: number[], question) => {
      if (question.taskFieldId) {
        return [...acc, question.taskFieldId];
      }

      return acc;
    }, []);
  }, [flattenedQuestions]);

  const availableTaskFields = useMemo(() => {
    const availableCustomeField = taskFields.filter(
      (cf) => !usedProjectCfIds.includes(cf.fieldId),
    );

    return mapProjectCustomFieldWithFormQuestion(availableCustomeField);
  }, [taskFields, usedProjectCfIds]);

  const usedTaskFields = useMemo(() => {
    const availableCustomeField = taskFields.filter((cf) =>
      usedProjectCfIds.includes(cf.fieldId),
    );

    return mapProjectCustomFieldWithFormQuestion(availableCustomeField);
  }, [taskFields, usedProjectCfIds]);

  return [availableTaskFields, usedTaskFields];
};
